import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

import React, { useRef, useMemo } from 'react';
import styled from 'styled-components';
import { theme, whenHovered, whenFocused } from "../../theme";
import { useForkedRef } from "../../hooks/useForkedRef/useForkedRef";
import { useTab } from "./useTab";
import { textStyles } from "../Text";
import { motion } from 'framer-motion';
import { mergeProps } from '@react-aria/utils';
import { eases } from "../../motion/eases";
export var Tab = React.forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
      onClick = _ref.onClick,
      badge = _ref.badge,
      disabled = _ref.disabled,
      other = _objectWithoutProperties(_ref, ["children", "onClick", "badge", "disabled"]); // Register our tab with parent


  var ownRef = useRef(null);
  var ref = useForkedRef(forwardedRef, ownRef);
  /**
   * Bind tab values, including accessibilty attributes
   */

  var _useTab = useTab(ownRef, {
    onClick: onClick
  }),
      bindTab = _useTab.bind,
      isSelected = _useTab.isSelected;

  var renderedBadge = useMemo(function () {
    if (badge && React.isValidElement(badge)) {
      return /*#__PURE__*/React.createElement(BadgeContainer, {
        transition: {
          duration: 0.3,
          ease: eases.primary
        },
        animate: {
          width: badge.props.isShowing ? 'auto' : 0
        }
      }, /*#__PURE__*/React.createElement(BadgeMargin, null, React.cloneElement(badge, _objectSpread({
        backgroundColor: 'tintA30'
      }, badge.props))));
    }

    return null;
  }, [badge]);
  var bind = mergeProps(bindTab, other);
  return /*#__PURE__*/React.createElement(StyledTab, _extends({
    ref: ref,
    $isActive: isSelected,
    disabled: disabled
  }, bind), /*#__PURE__*/React.createElement("span", null, children), renderedBadge);
});
var primaryEasing = theme.transitions.eases.primary;
export var StyledTab = styled.button.withConfig({
  displayName: "Tab__StyledTab",
  componentId: "vapor__sc-6rav2j-0"
})(["box-sizing:border-box;text-align:center;user-select:none;outline:none;-webkit-tap-highlight-color:transparent;box-shadow:none;cursor:pointer;text-decoration:none;background:transparent;border-width:initial;border-style:none;border-color:initial;border-image:initial;text-size-adjust:none;", ";position:relative;height:", ";line-height:", ";flex:1;opacity:1;display:inline-flex;justify-content:center;align-items:center;padding:0 ", ";border-radius:", ";white-space:nowrap;text-overflow:ellipsis;overflow:hidden;box-shadow:inset 0 0 0 1px rgba(255,255,255,0);transition:box-shadow ", " ", ";span{color:", ";overflow:hidden;text-overflow:ellipsis;position:relative;transition:color ", " ", ",opacity ", " ", ";}", " span{color:", ";}", "{box-shadow:", ";}&[disabled]{pointer-events:none;opacity:0.4;}"], textStyles.link, theme.spaces[32], theme.spaces[32], theme.spaces[16], theme.radii[8], theme.transitions.durations[300], primaryEasing, function (p) {
  return p.$isActive ? theme.colors.text.body : theme.colors.text.tertiary;
}, theme.transitions.durations[250], primaryEasing, theme.transitions.durations[250], primaryEasing, whenHovered, theme.colors.text.body, whenFocused, theme.shadows.interactive.focus);
var BadgeContainer = styled(motion.div).withConfig({
  displayName: "Tab__BadgeContainer",
  componentId: "vapor__sc-6rav2j-1"
})(["display:flex;justify-content:center;"]);
var BadgeMargin = styled.div.withConfig({
  displayName: "Tab__BadgeMargin",
  componentId: "vapor__sc-6rav2j-2"
})(["margin-left:", ";display:flex;"], theme.spaces[4]);